import styled, {css, keyframes} from "styled-components";
import {ACTION_CONTROL_HEIGHT_PX, brand, fontFamilies, goldEdgeFor} from "theme/tokens";
import {LiveCardState} from "./instantStatus";

/**
 * The instant/P2P live card's own styles (design `.live-status` / `.live-dot` / `.live-sub`).
 *
 * Externalized when the card took the hosted success card's link layout (ui#2486): everything about the share
 * link itself comes from `SuccessCard.styles` and the share row's own module, and the recipients from
 * `LiveRecipients.styles` (kitecorp/transferify-ui#3320), so what is left here is the card's frame.
 */

/**
 * Design `@keyframes pulse`: a dot's glow, in the dot's own colour, so a themed dot glows in its theme's shade.
 * Shared with the recipient list's waiting dot (`LiveRecipients.styles`).
 */
export const pulse = (color: string) => keyframes`
	0% { box-shadow: 0 0 0 0 color-mix(in srgb, ${color} 50%, transparent); }
	70% { box-shadow: 0 0 0 9px transparent; }
	100% { box-shadow: 0 0 0 0 transparent; }
`;

/**
 * One rhythm down the card: every block 10px from the next. The approved prototype's 12 left the card with two
 * recipients scrolling inside its own box at 1366x768 (kitecorp/transferify-ui#3341).
 */
export const LiveCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: 10px;
	min-width: 0;
`;

export const Head = styled.div`
	display: grid;
	gap: 2px;
	min-width: 0;
`;

/**
 * The card's title with its dot. Green while the link works, whoever is or is not downloading (P11): the
 * recipient list says who is where, and the dot says the link is up. Yellow while signaling reconnects, when nobody
 * new can reach the sender (kitecorp/transferify-ui#3383, owner review). Amber once the transfer was terminated.
 *
 * The yellow is the theme's gold, which on a light card is too pale to carry its own shape, so it takes the gold
 * family's edge there (`goldEdgeFor`, transparent in dark mode). A border-box border keeps the dot 11px either way.
 */
export const LiveDot = styled.span<{$state: LiveCardState}>`
	box-sizing: border-box;
	width: 11px;
	height: 11px;
	border-radius: 50%;
	flex: none;
	${({theme, $state}) => {
		switch ($state) {
			case LiveCardState.Live:
				return css`
					background: ${brand.live};
					animation: ${pulse(brand.live)} 1.8s infinite;
				`;
			case LiveCardState.Reconnecting:
				return css`
					background: ${theme.tokens.gold};
					border: 1px solid ${goldEdgeFor(theme.tokens)};
					animation: ${pulse(theme.tokens.gold)} 1.6s infinite;
				`;
			case LiveCardState.Stopped:
				return css`
					background: ${theme.tokens.orange};
					animation: ${pulse(theme.tokens.orange)} 1.6s infinite;
				`;
		}
	}}

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

export const StatusTitle = styled.h2`
	margin: 0;
	display: flex;
	align-items: center;
	gap: 9px;
	font: 700 18px/1.3 ${fontFamilies.sans};
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};

	/* Focused programmatically once the keep-tab-open notice closes (kitecorp/transferify-ui#3724), never by
	   tabbing, so it paints no ring the way a real control does: the next Tab lands on the link box, which does. */
	&:focus {
		outline: none;
	}
`;

/**
 * Under the title, aligned with its words rather than its dot.
 *
 * Set a hundredth of an em tighter, as the title above it is, so the note keeps to one line in the 318px the
 * card has beside the campaign (ui#2888) on every platform: Linux Chromium's hinting rounds each glyph's
 * advance, and the note it replaced wrapped there to a second line the card's height budget never granted
 * (`e2e/p2p-success-coupon.spec.ts`).
 */
export const LiveSub = styled.p`
	margin: 0;
	padding-left: 20px;
	font-size: 13px;
	font-weight: 500;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * kitecorp/transferify-api-service#418: the transfer was suspended or deleted, so the live link and its
 * QR are gone rather than left copyable — a dead URL handed out is worse than none.
 */
export const TerminatedNote = styled.p`
	text-align: center;
	margin: 0;
	padding: 12px;
	border-radius: 14px;
	font-size: 13px;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink2};
	background: ${({theme}) => theme.tokens.field};
`;

/** ui#123: a seeded file died on disk — the sender must see it, or recipients fail silently. */
export const SeedingErrorNote = styled.p`
	text-align: center;
	margin: 0;
	padding: 10px 12px;
	border-radius: 12px;
	font-size: 13px;
	font-weight: 600;
	color: ${brand.error};
	background: ${({theme}) => theme.tokens.field};
	overflow-wrap: anywhere;
`;

/* ---- design `.submit.ghost` ---- */

/**
 * The design system's one control height, which the hosted card's "Send another" already stands at. This one was
 * still the 52px that `ACTION_CONTROL_HEIGHT_PX` retired, and the 8px went to the 1366x768 fit
 * (kitecorp/transferify-ui#3341).
 */
export const GhostSubmit = styled.button`
	width: 100%;
	min-height: ${ACTION_CONTROL_HEIGHT_PX}px;
	border: none;
	border-radius: 14px;
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink};
	font: 700 15px ${fontFamilies.sans};
	cursor: pointer;
	transition: 0.18s;

	&:hover {
		background: ${({theme}) => theme.tokens.line};
	}
`;
